import React, { useState, useEffect, useRef, useMemo } from 'react';
import { View, Text, StyleSheet, Dimensions, TouchableOpacity, Platform, ScrollView } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { MaterialCommunityIcons, FontAwesome5, Ionicons } from '@expo/vector-icons';
import { useBanners } from '../../../BannerManager';
import { colores } from '../../../../styles/variablesCss';

const { width: viewportWidth } = Dimensions.get('window');

const OfertasEspeciales = () => {
  const navigation = useNavigation();
  const { banners: allBanners } = useBanners();
  const scrollViewRef = useRef(null);
  
  // Estado para controlar responsividad con breakpoints granulares
  const [windowWidth, setWindowWidth] = useState(
    Platform.OS === 'web' && typeof window !== 'undefined' ? window.innerWidth : 768
  );
  
  // Breakpoints responsivos
  const isExtraSmall = windowWidth < 400;  // Móviles muy pequeños
  const isSmall = windowWidth < 576;       // Móviles pequeños
  const isMobile = windowWidth < 768;      // Vista de 1 tarjeta
  const isTablet = windowWidth >= 768 && windowWidth < 992; // Tablets
  const isDesktopWide = windowWidth >= 1400;    // Desktop con espacio para 3 tarjetas

  const layoutMode = useMemo(() => {
    if (isDesktopWide) return 'grid';
    if (windowWidth >= 768) return 'carousel-2';
    return 'carousel-1';
  }, [isDesktopWide, windowWidth]);
  
  // Calcular altura de las tarjetas según tamaño de pantalla
  const getCardHeight = () => {
    if (isExtraSmall) return 80;
    if (isSmall) return 90;
    if (isMobile) return 100;
    if (isTablet) return 100;
    return 100;
  };
  
  // Calcular el espacio horizontal y vertical entre tarjetas para que se reduzca al achicar la pantalla
  const getGridGap = () => {
    if (windowWidth >= 1400) return 56;
    if (windowWidth >= 1200) return 48;
    if (windowWidth >= 992) return 36;
    if (windowWidth >= 768) return 28;
    return 20;
  };

  // Calcular ancho de tarjeta en desktop según cantidad de ofertas
  const getCardWidth = () => {
    if (layoutMode !== 'grid') {
      return '100%';
    }
    if (ofertas.length === 1) return '80%';
    if (ofertas.length === 2) return '48%';
    return '31%'; // 3 o más
  };
  
  // Estado para el carrusel en móvil o tablet
  const [currentIndex, setCurrentIndex] = useState(0);

  // Obtener banners de fila6
  const bannersFila6 = allBanners.fila6 || [];
  
  // Iconos predeterminados para cada posición
  const defaultIcons = [
    { iconComponent: FontAwesome5, iconName: 'baby' },
    { iconComponent: FontAwesome5, iconName: 'wine-glass-alt' },
    { iconComponent: Ionicons, iconName: 'basket-outline' }
  ];
  
  // Crear ofertas a partir de los banners de fila6
  const ofertas = bannersFila6.map((banner, index) => {
    // Usar el índice para elegir iconos predeterminados
    const defaultIcon = defaultIcons[index] || { iconComponent: MaterialCommunityIcons, iconName: 'tag-outline' };
    
    return {
      id: banner.posicion, // Usar posición como id
      titulo: banner.titulo || `OFERTA ESPECIAL ${index + 1}`,
      descripcion: banner.descripcion || 'Descuentos exclusivos para nuestros clientes',
      iconComponent: defaultIcon.iconComponent,
      iconName: defaultIcon.iconName,
      onPress: () => {
        const isDesktop = windowWidth >= 600;
        
        if (Platform.OS === 'web' && isDesktop && typeof window !== 'undefined') {
          // En desktop, abrir en pestaña nueva
          const url = `/promocion-detalle?id=${banner.posicion + 2}&titulo=${encodeURIComponent(banner.titulo || `Oferta Especial ${index + 1}`)}`;
          const fullUrl = window.location.origin + url;
          window.open(fullUrl, '_blank', 'noopener,noreferrer');
        } else {
          // En móvil, usar navigation.navigate
          navigation.navigate('PromocionDetalle', { 
            id: banner.posicion + 2,
            titulo: banner.titulo || `Oferta Especial ${index + 1}` 
          });
        }
      },
      // Guardar la referencia al banner original para usar otros campos si es necesario
      banner: banner
    };
  });

  // Listener para cambios de tamaño de ventana
  useEffect(() => {
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      const handleResize = () => {
        setWindowWidth(window.innerWidth);
      };
      
      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    }
  }, []);

  // Navegar en el carrusel
  const getSlidesPerView = () => {
    if (layoutMode === 'carousel-2') {
      return ofertas.length >= 2 ? 2 : 1;
    }
    return 1;
  };

  const getPageCount = () => {
    if (layoutMode === 'grid') return 0;
    const perView = getSlidesPerView();
    return Math.max(1, ofertas.length - perView + 1);
  };

  const navegarCarrusel = (direction) => {
    const slidesPerView = getSlidesPerView();
    const maxIndex = Math.max(0, ofertas.length - slidesPerView);
    let newIndex = currentIndex;
    if (direction === 'next') {
      newIndex = currentIndex < maxIndex ? currentIndex + 1 : 0;
    } else {
      newIndex = currentIndex > 0 ? currentIndex - 1 : maxIndex;
    }
    setCurrentIndex(newIndex);

    if (scrollViewRef.current) {
      const slideWidth = layoutMode === 'carousel-2' ? windowWidth / slidesPerView : windowWidth;
      scrollViewRef.current.scrollTo({
        x: newIndex * slideWidth,
        animated: true
      });
    }
  };

  // Auto-play del carrusel en móvil
  useEffect(() => {
    if (layoutMode !== 'grid' && ofertas.length > 1) {
      const interval = setInterval(() => {
        navegarCarrusel('next');
      }, 4000); // Cambia cada 4 segundos
      
      return () => clearInterval(interval);
    }
  }, [currentIndex, layoutMode, windowWidth, ofertas.length]);

  useEffect(() => {
    if (layoutMode === 'grid') {
      setCurrentIndex(0);
      return;
    }
    const perView = getSlidesPerView();
    const maxIndex = Math.max(0, ofertas.length - perView);
    if (currentIndex > maxIndex) {
      setCurrentIndex(0);
      scrollViewRef.current?.scrollTo({ x: 0, animated: false });
    }
  }, [layoutMode, windowWidth, ofertas.length]);

  // Si no hay banners en fila6, mostrar mensaje
  if (ofertas.length === 0) {
    return (
      <View style={styles.wrapper}>
        <View style={styles.container}>
          <Text style={styles.noDataText}>No hay ofertas especiales disponibles</Text>
        </View>
      </View>
    );
  }

  const cardHeight = getCardHeight();
  const cardWidth = getCardWidth();
  const gridGap = getGridGap();
  const slidesPerView = getSlidesPerView();
  const slideWidth = layoutMode === 'carousel-2' ? windowWidth / slidesPerView : windowWidth;
  const pageCount = getPageCount();
  const shouldForceSingleRow = layoutMode === 'grid' && ofertas.length <= 3;

  // Renderizar tarjeta individual
  const renderOferta = (oferta, index) => {
        const IconComponent = oferta.iconComponent;
        // Si el banner tiene un link, usarlo para la navegación
        const handlePress = () => {
          const isDesktop = windowWidth >= 600;
          const abrirEnPestañaNueva = oferta.banner?.apertura === 1;
          
          // Si el banner tiene un link definido, redirigir a ese link
          if (oferta.banner && oferta.banner.link && oferta.banner.link.trim() !== '') {
            if (typeof window !== 'undefined' && typeof document !== 'undefined') {
              // Extraer solo la parte de la URL después del dominio
              const linkUrl = oferta.banner.link.includes('pinguino.com.ar') 
                ? oferta.banner.link.split('pinguino.com.ar')[1] 
                : oferta.banner.link;
              
              // Determinar si es URL externa
              const isExternalUrl = oferta.banner.link.startsWith('http://') || oferta.banner.link.startsWith('https://');
              const finalUrl = isExternalUrl ? oferta.banner.link : (window.location.origin + linkUrl);
              
              // Si apertura es 1, abrir en pestaña nueva (solo en desktop)
              if (abrirEnPestañaNueva && isDesktop) {
                window.open(finalUrl, '_blank', 'noopener,noreferrer');
                return;
              }
              
              // Si apertura es 0 o estamos en móvil, navegar en la misma pestaña
              if (isExternalUrl) {
                window.location.href = finalUrl;
              } else {
                const isOnProductsPage = window.location.pathname === '/productos';
                
                if (isOnProductsPage) {
                  window.history.pushState({}, '', linkUrl);
                  window.dispatchEvent(new Event('navigationChange'));
                } else {
                  window.location.href = finalUrl;
                }
              }
              
              return;
            }
          }

          // Si no hay link, usar la lógica actual de navegación con codBanner
          if (oferta.banner && oferta.banner.codBanner) {
            // Si estamos en plataforma web, usar history API para actualizar URL con codBanner
            if (typeof window !== 'undefined' && typeof document !== 'undefined') {
              // Construir URL con parámetros - siempre incluir todos los parámetros
              const urlParams = new URLSearchParams();
              
              // Siempre incluir D, C, S como 0 (por defecto)
              urlParams.append('D', 0);
              urlParams.append('C', 0);
              urlParams.append('S', 0);
              
              // Agregar codBanner usando el ID del banner
              urlParams.append('codBanner', oferta.banner.codBanner);
              urlParams.append('searchText', '');
              
              const url = `/productos?${urlParams.toString()}`;
              
              // Si apertura es 1, abrir en pestaña nueva (solo en desktop)
              if (abrirEnPestañaNueva && isDesktop) {
                const fullUrl = window.location.origin + url;
                window.open(fullUrl, '_blank', 'noopener,noreferrer');
                return;
              }
              
              // Si apertura es 0 o estamos en móvil, navegar en la misma pestaña
              const isOnProductsPage = window.location.pathname === '/productos';
              
              if (isOnProductsPage) {
                window.history.pushState({}, '', url);
                window.dispatchEvent(new Event('navigationChange'));
              } else {
                window.location.href = url;
              }
              
              return;
            }
          }
          
          // Fallback: usar el onPress predeterminado
          oferta.onPress();
        };
        
    return (
      <View
        key={oferta.id}
        style={[
          layoutMode === 'grid' ? styles.cardContainer : styles.cardContainerMobile,
          layoutMode === 'grid'
            ? { width: cardWidth }
            : { width: '92%' }
        ]}
      >
        <TouchableOpacity 
          style={[styles.ofertaItem, { height: cardHeight }]}
          onPress={handlePress}
          activeOpacity={0.8}
        >
          <View style={styles.headerContainer}>
            <Text style={[
              styles.tituloOferta,
              isExtraSmall && { fontSize: 14 },
              isSmall && { fontSize: 16 },
              isMobile && !isSmall && !isExtraSmall && { fontSize: 18 }
            ]}>{oferta.titulo}</Text>
          </View>
          {/*<View style={styles.textContainer}>
            <Text style={[
              styles.descripcion,
              isExtraSmall && { fontSize: 9, lineHeight: 12 },
              isSmall && { fontSize: 10, lineHeight: 13 }
            ]}>{oferta.descripcion}</Text>
          </View>*/}
        </TouchableOpacity>
      </View>
    );
  };

  return (
    <View style={styles.wrapper}>
      {layoutMode === 'grid' ? (
        // Vista de grilla para desktop (>=1200 px)
        <View style={[
          styles.container,
          { gap: gridGap },
          ofertas.length === 1 && styles.singleCardRow,
          shouldForceSingleRow && styles.singleRow
        ]}>
          {ofertas.map((oferta, index) => renderOferta(oferta, index))}
        </View>
      ) : (
        // Vista de carrusel para móvil/tablet
        <View style={styles.carouselContainer}>
          <ScrollView
            ref={scrollViewRef}
            horizontal
            pagingEnabled
            showsHorizontalScrollIndicator={false}
            onMomentumScrollEnd={(event) => {
              const pageWidth = slideWidth;
              const newIndex = Math.round(event.nativeEvent.contentOffset.x / pageWidth);
              setCurrentIndex(newIndex);
            }}
            scrollEventThrottle={16}
            style={[styles.scrollView, { width: windowWidth }, Platform.OS === 'web' && { touchAction: 'pan-y' }]}
            directionalLockEnabled={true}
          >
            {ofertas.map((oferta, index) => (
              <View key={oferta.id} style={[styles.mobileSlide, { width: slideWidth }]}>
                {renderOferta(oferta, index)}
              </View>
            ))}
          </ScrollView>
          
          {/* Indicadores de posición */}
          {pageCount > 0 && (
            <View style={styles.indicatorsContainer}>
              {Array.from({ length: pageCount }).map((_, index) => (
                <TouchableOpacity
                  key={index}
                  style={[
                    styles.indicator,
                    index === currentIndex && styles.indicatorActive
                  ]}
                  onPress={() => {
                    setCurrentIndex(index);
                    scrollViewRef.current?.scrollTo({
                      x: index * slideWidth,
                      animated: true
                    });
                  }}
                />
              ))}
            </View>
          )}
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  wrapper: {
    paddingBottom: 40,
    width: '100%',
    overflow: 'hidden',
  },
  carouselContainer: {
    width: '100%',
    position: 'relative',
    alignItems: 'center',
    //overflow: 'hidden',
  },
  scrollView: {
    // width se aplica inline con windowWidth
  },
  
  mobileSlide: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  container: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: 48,
    //paddingHorizontal: 10,
    width: '100%',
  },
  singleCardRow: {
    justifyContent: 'center',
  },
  singleRow: {
    flexWrap: 'nowrap',
  },
  cardContainer: {
    // Width ahora se calcula dinámicamente
    minWidth: 250,
    maxWidth: 400,
  },
  cardContainerMobile: {
    width: '92%',
    maxWidth: 450,
    alignSelf: 'center',
  },
  indicatorsContainer: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: -16,
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 8,
    ...(Platform.OS === 'web' ? { pointerEvents: 'auto' } : {}),
  },
  indicator: {
    width: 8,
    height: 8,
    //borderRadius: 4,
    backgroundColor: '#D0D0D0',
  },
  indicatorActive: {
    backgroundColor: colores.rojo,
    width: 24,
  },
  noDataText: {
    flex: 1,
    textAlign: 'center',
    color: colores.texto,
    padding: 20,
    fontSize: 14,
  },
  ofertaItem: {
    width: '100%',
    // Height ahora se calcula dinámicamente
    //borderRadius: 10,
    overflow: 'hidden',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: colores.blanco,
    flexDirection: 'column',
    paddingHorizontal: 12,
    paddingVertical: 12,
    ...(Platform.OS === 'web'
      ? { boxShadow: '0 2px 8px rgba(0,0,0,0.12)' }
      : {
          shadowColor: '#000',
          shadowOffset: { width: 0, height: 2 },
          shadowOpacity: 0.12,
          shadowRadius: 5,
          elevation: 3,
        }
    ),
  },
  headerContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    width: '80%',
    marginHorizontal: 'auto',
    marginBottom: 10,
  },
  iconContainer: {
    width: 45,
    height: 45,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 8,
  },
  textContainer: {
    width: '100%',
    alignItems: 'center',
    justifyContent: 'center',
  },
  tituloOferta: {
    fontSize: 18,
    fontWeight: 'bold',
    color: colores.rojo,
    textAlign: 'center',
    textTransform: 'uppercase',
  },
  descripcion: {
    fontSize: 11,
    color: colores.texto,
    flexWrap: 'wrap',
    lineHeight: 14,
    textAlign: 'center',
    maxHeight: 42,
    overflow: 'hidden',
    textAlign: 'center',
  }
});
  
  export default OfertasEspeciales;